import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AdminProvider } from '../../../context/AdminContext';
import '../../../styles/components/admin/AdminLayout.css';

// Import logo
import logoImage from '../../../assets/logo/neurolex_logo_new2.png';

// Import icons
import dashboardIcon from '../../../assets/icons/dashboard_icon.svg';
import usersIcon from '../../../assets/icons/UserManagement_Icon.svg';
import doctorIcon from '../../../assets/icons/DoctorManagement_Icon.svg';
import journalIcon from '../../../assets/icons/journal_icon.svg';
import tenantIcon from '../../../assets/icons/clinic_icon.svg';
import settingsIcon from '../../../assets/icons/Settings_icon.svg';
import moodIcon from '../../../assets/icons/mood_icon.svg';
import profileIcon from '../../../assets/icons/admin_icon.svg';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });

  // Custom active check function for nested routes
  const isActiveRoute = (path, menuId) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    
    if (menuId === 'users') {
      return location.pathname.startsWith('/admin/users') || 
             location.pathname.startsWith('/admin/patients');
    }
    
    if (menuId === 'journal') {
      return location.pathname.startsWith('/admin/templates') || 
             location.pathname.startsWith('/admin/journal-entries');
    }

    if (menuId === 'mood') {
      return location.pathname.startsWith('/admin/mood-checkins');
    }

    if (menuId === 'profile') {
      return location.pathname.startsWith('/admin/profile');
    }
    
    return location.pathname.startsWith(path);
  };

  const handleProfileClick = () => {
    console.log('Profile button clicked!');
    navigate('/admin/profile');
  };

  // Handle mouse enter to calculate tooltip position
  const handleMouseEnter = (e, itemId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({ top: rect.top + rect.height / 2 });
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin', icon: dashboardIcon },
    { id: 'users', label: 'User Management', path: '/admin/users', icon: usersIcon },
    { id: 'professionals', label: 'Doctor Management', path: '/admin/professionals', icon: doctorIcon },
    { id: 'mood', label: 'Mood Analytics', path: '/admin/mood-checkins', icon: moodIcon },
    { id: 'journal', label: 'Journal Management', path: '/admin/journal-entries', icon: journalIcon }, 
    { id: 'tenants', label: 'Tenant Management', path: '/admin/tenants', icon: tenantIcon },
    { id: 'settings', label: 'System Settings', path: '/admin/settings', icon: settingsIcon }
  ];

  return (
    <AdminProvider>
      <div className="admin-layout">
        <div className="admin-sidebar">
          <div className="sidebar-top-content">
            <div className="sidebar-top">
              <div className="logo-container">
                <img src={logoImage} alt="Neurolex Logo" className="admin-logo" />
              </div>
            </div>
            
            <div className="sidebar-nav">
              <ul className="nav-menu">
                {menuItems.map(item => (
                  <li key={item.id} className="nav-item">
                    <NavLink 
                      to={item.path}
                      className={() => {
                        const shouldBeActive = isActiveRoute(item.path, item.id);
                        return shouldBeActive ? "nav-link active" : "nav-link";
                      }}
                      end={item.path === '/admin'}
                      title={item.label}
                      onMouseEnter={(e) => handleMouseEnter(e, item.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <img src={item.icon} alt={item.label} className="nav-icon" />
                      {hoveredItem === item.id && (
                        <span 
                          className="nav-tooltip"
                          style={{ top: `${tooltipPosition.top}px` }}
                        >
                          {item.label}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="sidebar-bottom">
            <button 
              className={`profile-container ${isActiveRoute('/admin/profile', 'profile') ? 'active' : ''}`}
              onClick={handleProfileClick}
              title="Profile Settings"
              onMouseEnter={(e) => handleMouseEnter(e, 'profile')}
              onMouseLeave={handleMouseLeave}
            >
              <img 
                src={profileIcon} 
                alt="Profile Settings" 
                className="profile-icon-img"
              />
              {hoveredItem === 'profile' && (
                <span 
                  className="nav-tooltip"
                  style={{ top: `${tooltipPosition.top}px` }}
                >
                  Profile Settings
                </span>
              )}
            </button>
          </div>
        </div>
        
        <div className="admin-content">
          <div className="admin-content-container">
            <Outlet />
          </div>
        </div>
      </div>
    </AdminProvider>
  );
};

export default AdminLayout;